import { Box } from '@mui/material'
import { AutocompleteStaticOptions, AutocompleteStaticOptionType } from 'components/core/autocomplete/AutocompleteStaticOptions'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1LabeledSelectStyles } from 'components/core/RHKFormComponents/C1LabeledSelectStyles'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { Report } from 'components/pages/explore/query/Report'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { Search } from 'lucide-react'
import { useRouter } from 'next/router'
import React, { useMemo, useState, useEffect } from 'react'
import { IntlShape, useIntl } from 'react-intl'

export const QUERIES = {
    AccessProfileMembership: 'access-profile-membership',
    AppUsersWithoutOwner: 'accounts-without-owner',
    AllAppUsers: 'search-all-accounts',
    Resources: 'search-resources ',
    HighRiskRoleGrantsPermanent: 'high-risk-role-grants-permanent',
    HighRiskRoleGrantsTemporary: 'high-risk-role-grants-temporary',
    UsersWithoutManager: 'users-without-manager',
    UsersWithUnspecifiedEmploymentStatus: 'users-with-unspecified-employment-status',
    AppUsersNotLoggedInRecently: 'accounts-inactive',
    OrphanedAccounts: 'orphaned-accounts',
    AppEntitlementsWithOneOwner: 'app-entitlements-with-one-owner',
    HighRiskAccounts: 'high-risk-accounts',
    StandingPrivileges: 'standing-privileges',
    AppsWithOneOwner: 'apps-with-one-owner',
    AppsWithDeactivatedOwners: 'deactivated-owners-accounts',
    AppEntitlementsWithDeactivatedOwner: 'app-entitlements-with-deactivated-owner',
    AppResourcesWithDeactivatedOwner: 'app-resources-with-deactivated-owner',
    PastGrants: 'past-grants',
    ActiveExternalAccounts: 'active-external-accounts',
}

export const queryToLabel = (intl: IntlShape, query: string): string => {
    switch (query) {
        case QUERIES.AccessProfileMembership:
            return intl.formatMessage({
                id: 'structured_search_access_profile_membership',
                defaultMessage: 'Access profile memberships',
            })
        case QUERIES.AppUsersWithoutOwner:
            return intl.formatMessage({
                id: 'structured_search_accounts_without_owner',
                defaultMessage: 'Accounts without an account owner',
            })
        case QUERIES.AllAppUsers:
            return intl.formatMessage({
                id: 'structured_search_accounts',
                defaultMessage: 'All accounts',
            })
        case QUERIES.Resources:
            return intl.formatMessage({
                id: 'structured_search_resources',
                defaultMessage: 'All resources',
            })
        case QUERIES.HighRiskRoleGrantsPermanent:
            return intl.formatMessage({
                id: 'structured_search_high_risk_role_grants_permanent',
                defaultMessage: 'High-risk role grants (permanent)',
            })
        case QUERIES.HighRiskRoleGrantsTemporary:
            return intl.formatMessage({
                id: 'structured_search_high_risk_role_grants_temporary',
                defaultMessage: 'High-risk role grants (temporary)',
            })
        case QUERIES.UsersWithoutManager:
            return intl.formatMessage({
                id: 'structured_search_users_without_manager',
                defaultMessage: 'Users without a manager',
            })
        case QUERIES.UsersWithUnspecifiedEmploymentStatus:
            return intl.formatMessage({
                id: 'structured_search_users_with_unspecified_employment_status',
                defaultMessage: 'Users with an unspecified employment status',
            })
        case QUERIES.AppUsersNotLoggedInRecently:
            return intl.formatMessage({
                id: 'structured_search_accounts_inactive',
                defaultMessage: 'Inactive accounts',
            })
        case QUERIES.OrphanedAccounts:
            return intl.formatMessage({
                id: 'structured_search_orphaned_accounts',
                defaultMessage: 'Orphaned accounts',
            })
        case QUERIES.AppEntitlementsWithOneOwner:
            return intl.formatMessage({
                id: 'structured_search_app_entitlements_with_one_owner',
                defaultMessage: 'Entitlements with one owner',
            })
        case QUERIES.HighRiskAccounts:
            return intl.formatMessage({
                id: 'structured_search_high_risk_accounts',
                defaultMessage: 'High-risk accounts',
            })
        case QUERIES.StandingPrivileges:
            return intl.formatMessage({
                id: 'structured_search_standing_privileges',
                defaultMessage: 'Standing privileges',
            })
        case QUERIES.AppsWithOneOwner:
            return intl.formatMessage({
                id: 'structured_search_apps_with_one_owner',
                defaultMessage: 'Apps with one owner',
            })
        case QUERIES.AppsWithDeactivatedOwners:
            return intl.formatMessage({
                id: 'structured_search_apps_with_deactivated_owners',
                defaultMessage: 'Apps with a deactivated owner',
            })
        case QUERIES.AppEntitlementsWithDeactivatedOwner:
            return intl.formatMessage({
                id: 'structured_search_app_entitlements_with_deactivated_owner',
                defaultMessage: 'Entitlements with a deactivated owner',
            })
        case QUERIES.AppResourcesWithDeactivatedOwner:
            return intl.formatMessage({
                id: 'structured_search_app_resources_with_deactivated_owner',
                defaultMessage: 'Resources with a deactivated owner',
            })
        case QUERIES.PastGrants:
            return intl.formatMessage({
                id: 'structured_search_past_grants',
                defaultMessage: 'Past grants',
            })
        case QUERIES.ActiveExternalAccounts:
            return intl.formatMessage({
                id: 'structured_search_active_external_accounts',
                defaultMessage: 'Active external accounts',
            })
        default:
            return intl.formatMessage({
                id: 'structured_search_unknown',
                defaultMessage: 'Unknown',
            })
    }
}

const QueryCategories = {
    AccessProfiles: 'Access profiles',
    Accounts: 'Accounts',
    Apps: 'Apps',
    Entitlements: 'Entitlements',
    Resources: 'Resources',
    Roles: 'Roles',
    Users: 'Users',
}

export const Query = (): React.ReactElement | null => {
    const router = useRouter()
    const intl = useIntl()

    const options = useMemo(() => [
        {
            category: QueryCategories.AccessProfiles,
            label: queryToLabel(intl, QUERIES.AccessProfileMembership),
            value: QUERIES.AccessProfileMembership,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.AllAppUsers),
            value: QUERIES.AllAppUsers,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.AppUsersNotLoggedInRecently),
            value: QUERIES.AppUsersNotLoggedInRecently,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.AppUsersWithoutOwner),
            value: QUERIES.AppUsersWithoutOwner,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.HighRiskAccounts),
            value: QUERIES.HighRiskAccounts,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.OrphanedAccounts),
            value: QUERIES.OrphanedAccounts,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.ActiveExternalAccounts),
            value: QUERIES.ActiveExternalAccounts,
        },
        {
            category: QueryCategories.Accounts,
            label: queryToLabel(intl, QUERIES.PastGrants),
            value: QUERIES.PastGrants,
        },
        {
            category: QueryCategories.Apps,
            label: queryToLabel(intl, QUERIES.AppsWithDeactivatedOwners),
            value: QUERIES.AppsWithDeactivatedOwners,
        },
        {
            category: QueryCategories.Apps,
            label: queryToLabel(intl, QUERIES.AppsWithOneOwner),
            value: QUERIES.AppsWithOneOwner,
        },
        {
            category: QueryCategories.Entitlements,
            label: queryToLabel(intl, QUERIES.AppEntitlementsWithDeactivatedOwner),
            value: QUERIES.AppEntitlementsWithDeactivatedOwner,
        },
        {
            category: QueryCategories.Entitlements,
            label: queryToLabel(intl, QUERIES.AppEntitlementsWithOneOwner),
            value: QUERIES.AppEntitlementsWithOneOwner,
        },
        {
            category: QueryCategories.Resources,
            label: queryToLabel(intl, QUERIES.Resources),
            value: QUERIES.Resources,
        },
        {
            category: QueryCategories.Resources,
            label: queryToLabel(intl, QUERIES.AppResourcesWithDeactivatedOwner),
            value: QUERIES.AppResourcesWithDeactivatedOwner,
        },
        {
            category: QueryCategories.Roles,
            label: queryToLabel(intl, QUERIES.HighRiskRoleGrantsPermanent),
            value: QUERIES.HighRiskRoleGrantsPermanent,
        },
        {
            category: QueryCategories.Roles,
            label: queryToLabel(intl, QUERIES.HighRiskRoleGrantsTemporary),
            value: QUERIES.HighRiskRoleGrantsTemporary,
        },
        {
            category: QueryCategories.Users,
            label: queryToLabel(intl, QUERIES.StandingPrivileges),
            value: QUERIES.StandingPrivileges,
        },
        {
            category: QueryCategories.Users,
            label: queryToLabel(intl, QUERIES.UsersWithoutManager),
            value: QUERIES.UsersWithoutManager,
        },
        {
            category: QueryCategories.Users,
            label: queryToLabel(intl, QUERIES.UsersWithUnspecifiedEmploymentStatus),
            value: QUERIES.UsersWithUnspecifiedEmploymentStatus,
        },
    ], [intl])

    const [selectedQuery, setSelectedQuery] = useState<AutocompleteStaticOptionType | null>(() => {
        const { queryId } = router.query
        for (const o of options) {
            if (o.value === queryId) {
                return o
            }
        }
        return null
    })

    useEffect(() => {
        const { queryId } = router.query
        if (queryId && typeof queryId === 'string') {
            const option = options.find((o) => o.value === queryId)
            if (option) {
                setSelectedQuery(option)
            } else {
                setSelectedQuery(null)
            }
        }
    }, [router.query, options])

    const handleQueryChange = (_: React.SyntheticEvent<Element, Event>, newValue: AutocompleteStaticOptionType | null): void => {
        setSelectedQuery(newValue)
        router.query = {}
        router.query = newValue ? { queryId: newValue.value } : {}
        router.replace({ pathname: router.pathname, query: router.query }, undefined, { shallow: true })
    }

    return (
        <Box sx={sxc(StructuredSearchStyles.fitContent)}>
            <Box sx={sxc(C1LabeledSelectStyles.selectOverrides, StructuredSearchStyles.queryContainer)}>
                <AutocompleteStaticOptions
                    label={
                        intl.formatMessage({
                            id: 'structured_search_query_label',
                            defaultMessage: 'Show me...',
                        })
                    }
                    value={selectedQuery}
                    options={options}
                    handleChange={handleQueryChange}
                />
            </Box>
            <Box sx={sxc(StructuredSearchStyles.fitContent, StructuredSearchStyles.queryViewContainer)}>
                {selectedQuery
                    ? <Report query={selectedQuery?.value} params={{}} />
                    : <EmptyList
                        icon={<C1Icon icon={Search} />}
                        text={intl.formatMessage({
                            id: 'structured_search_empty_text',
                            defaultMessage: 'Select a query to get started',
                        })}
                    />
                }
            </Box>
        </Box>
    )
}
